import { Controller } from "@hotwired/stimulus";
import { GroupProfileDomHelpers } from "../services/GroupProfileDomHelpers";
import { GroupProfilePriceUpdateService } from "../services/GroupProfilePriceUpdateService";

/**
 * Price Controller for Product Group Profile Step
 *
 * Responsibilities:
 * - Fetch updated prices from server when selections change
 * - Update booking summary with missing selections
 * - Display total price preview
 * - Handle price reset when form becomes invalid
 * - Manage loading state during price updates
 * - Collect form selections for price calculation
 *
 * Events listened:
 * - group-profile:quantity-changed - Update price when quantity changes
 * - group-profile:accommodation-changed - Update price when accommodation changes
 * - group-profile:validation-changed - Update price when validation state changes
 * - booking-price-update-needed - Global price update request
 * - booking-price-reset-needed - Global price reset request
 *
 * Events dispatched:
 * - group-profile:price-updated - When price is successfully updated
 * - group-profile:price-reset - When price is reset to default
 */
export default class ProductGroupProfilePriceController extends Controller {
  static values = {
    bookingId: Number,
    xOfYSelected: {
      type: String,
      default: "{selected} of {total} elements selected",
    },
    missingSelectionText: {
      type: String,
      default: "Missing Selection",
    },
    missingSelectionDetailsText: {
      type: String,
      default: "{missing} of {total} selections missing",
    },
  };

  connect() {
    this.isInitializing = true;

    this.setupPriceListeners();
    this.setupInputListeners();

    this.updateBookingSummary();

    this.isInitializing = false;
  }

  setupPriceListeners() {
    this.element.addEventListener(
      "group-profile:existing-selections-loaded",
      () => {
        this.existingSelectionsLoaded = true;
      }
    );

    this.element.addEventListener("group-profile:quantity-changed", () => {
      this.handlePriceUpdateTrigger();
    });

    this.element.addEventListener("group-profile:accommodation-changed", () => {
      this.handlePriceUpdateTrigger();
    });

    this.element.addEventListener("group-profile:update-all-summaries", () => {
      this.updateAllGroupSummaries();
      this.triggerInitialPriceUpdate();
    });

    this.element.addEventListener("group-profile:room-selected", () => {
      this.updateBookingSummary();
    });

    this.element.addEventListener("group-profile:room-removed", () => {
      this.updateBookingSummary();
    });

    document.addEventListener("booking-price-update-needed", (event) => {
      if (event.detail.bookingId === this.bookingIdValue) {
        this.fetchUpdatedPrice(event.detail.formSelections);
      }
    });

    document.addEventListener("booking-price-reset-needed", (event) => {
      if (event.detail.bookingId === this.bookingIdValue) {
        this.handlePriceReset();
      }
    });
  }

  setupInputListeners() {
    const formInputs = this.element.querySelectorAll(
      ".product-group-profile-select, .product-group-profile-checkbox, .product-group-profile-quantity"
    );

    formInputs.forEach((input) => {
      input.addEventListener("input", () => {
        this.updateGroupSummary(input.dataset.groupId);
        this.handlePriceUpdateTrigger();
      });

      if (input.type === "checkbox") {
        input.addEventListener("change", () => {
          this.updateGroupSummary(input.dataset.groupId);
          this.handlePriceUpdateTrigger();
        });
      }
    });
  }

  handlePriceUpdateTrigger() {
    if (this.isInitializing) {
      return;
    }

    GroupProfilePriceUpdateService.schedulePriceUpdate(() => {
      if (this.isFormValidForPricing()) {
        this.fetchUpdatedPrice();
      } else {
        this.resetPriceToDefault();
      }
      this.updateBookingSummary();
    });
  }

  isFormValidForPricing() {
    const validationEvent = new CustomEvent("group-profile:validate-form", {
      bubbles: true,
      cancelable: true,
      detail: { submissionAttempted: false },
    });

    this.element.dispatchEvent(validationEvent);

    return !validationEvent.defaultPrevented;
  }

  triggerInitialPriceUpdate() {
    const isValid = this.isFormValidForPricing();

    if (isValid) {
      const formSelections = this.collectCurrentFormSelections();
      const priceUpdateEvent = new CustomEvent("booking-price-update-needed", {
        bubbles: true,
        detail: {
          bookingId: this.bookingIdValue,
          formSelections,
        },
      });

      document.dispatchEvent(priceUpdateEvent);
    }
  }

  fetchUpdatedPrice(formSelections = null) {
    const bookingId = this.bookingIdValue;
    if (!bookingId) return;

    if (!this.isFormValidForPricing()) {
      return;
    }

    const selections = formSelections || this.collectCurrentFormSelections();

    if (!GroupProfilePriceUpdateService.hasDataChanged(selections)) {
      return;
    }

    const priceUpdateEvent = new CustomEvent("booking-price-update-needed", {
      bubbles: true,
      detail: {
        bookingId: this.bookingIdValue,
        formSelections: selections,
      },
    });

    GroupProfilePriceUpdateService.setLastUpdateData(selections);

    document.dispatchEvent(priceUpdateEvent);

    GroupProfileDomHelpers.dispatchEvent(
      this.element,
      "group-profile:price-update-requested",
      {
        selections,
      }
    );
  }

  resetPriceToDefault() {
    GroupProfilePriceUpdateService.setLastUpdateData(null);

    const priceResetEvent = new CustomEvent("booking-price-reset-needed", {
      bubbles: true,
      detail: {
        bookingId: this.bookingIdValue,
      },
    });

    document.dispatchEvent(priceResetEvent);
  }

  handlePriceReset() {
    GroupProfileDomHelpers.dispatchEvent(
      this.element,
      "group-profile:price-reset"
    );
  }

  collectCurrentFormSelections() {
    const formData = new URLSearchParams();

    const quantityEvent = new CustomEvent("group-profile:get-quantities", {
      bubbles: true,
      cancelable: true,
      detail: { quantities: {} },
    });

    this.element.dispatchEvent(quantityEvent);

    const groupQuantities = quantityEvent.detail.quantities || {};

    Object.entries(groupQuantities).forEach(([groupId, quantities]) => {
      const groupElement = this.element.querySelector(
        `[data-group-id="${groupId}"]`
      );
      let isAccommodationGroup = false;

      if (groupElement) {
        const accommodationContainer = groupElement.closest(
          ".accommodation-container, [data-accommodation-container]"
        );
        if (accommodationContainer) {
          isAccommodationGroup = true;
        } else {
          const detailsElement = groupElement.closest("details");
          if (detailsElement) {
            const summaryText =
              detailsElement
                .querySelector("summary")
                ?.textContent.toLowerCase() || "";
            if (
              summaryText.includes("accommodation") ||
              summaryText.includes("unterkunft") ||
              summaryText.includes("zimmer")
            ) {
              isAccommodationGroup = true;
            }
          }
        }
      }

      if (!isAccommodationGroup && quantities) {
        Object.entries(quantities).forEach(([resourceSkuId, quantity]) => {
          if (quantity > 0) {
            const paramName = `resource_sku_${resourceSkuId}_quantity`;
            formData.append(paramName, quantity.toString());
          }
        });
      }
    });

    return formData;
  }

  updateBookingSummary() {
    this.updateMissingSelectionsSummary();
  }

  updateMissingSelectionsSummary() {
    const missingSectionContainer = this.element.querySelector(
      '[data-booking-summary-section="missing-selections"]'
    );

    if (!missingSectionContainer) {
      return;
    }

    const missingGroups = [];

    const roomAssignmentContainers =
      GroupProfileDomHelpers.findRoomAssignmentContainers(this.element);
    if (roomAssignmentContainers.length > 0) {
      let selectedRoomsCount = 0;
      const totalRoomsRequired = roomAssignmentContainers.length;

      roomAssignmentContainers.forEach((container) => {
        const hasSelection = container.querySelector(
          ".border-green-500.bg-green-50"
        );

        if (hasSelection) {
          selectedRoomsCount += 1;
        }
      });

      if (selectedRoomsCount < totalRoomsRequired) {
        const missingRoomsCount = totalRoomsRequired - selectedRoomsCount;
        missingGroups.push({
          groupId: "accommodation_rooms",
          groupLabel: "Zimmer",
          missingCount: missingRoomsCount,
          requiredCount: totalRoomsRequired,
          priority: 0,
        });
      }
    }

    const allGroupElements = this.element.querySelectorAll(
      ".product-group-profile-summary-label[data-group-id]"
    );

    const processedGroups = new Set();

    allGroupElements.forEach((element) => {
      const { groupId } = element.dataset;

      if (processedGroups.has(groupId)) {
        return;
      }
      processedGroups.add(groupId);

      const requiredCount = parseInt(element.dataset.requiredCount, 10) || 0;
      const selectedCount = parseInt(element.dataset.selectedCount, 10) || 0;

      if (selectedCount < requiredCount) {
        const missingCount = requiredCount - selectedCount;
        const groupLabel = this.getGroupLabel(groupId, element);

        missingGroups.push({
          groupId,
          groupLabel,
          missingCount,
          requiredCount,
          priority: 1,
        });
      }
    });

    missingGroups.sort((a, b) => (a.priority || 1) - (b.priority || 1));

    if (missingGroups.length === 0) {
      missingSectionContainer.innerHTML = "";
      missingSectionContainer.style.display = "none";
    } else {
      missingSectionContainer.style.display = "block";
      missingSectionContainer.innerHTML = "";

      const headerElement = document.createElement("h4");
      headerElement.className = "text-sm font-medium text-red-600 mb-2";
      headerElement.textContent = this.missingSelectionTextValue;
      missingSectionContainer.appendChild(headerElement);

      const contentContainer = document.createElement("div");
      contentContainer.className = "space-y-1";
      missingSectionContainer.appendChild(contentContainer);

      missingGroups.forEach((missing) => {
        const missingDiv = document.createElement("div");
        missingDiv.className = "text-xs text-red-600";
        missingDiv.setAttribute("data-missing-group", missing.groupId);
        missingDiv.setAttribute("data-required-count", missing.requiredCount);
        const detailsText = this.missingSelectionDetailsTextValue
          .replace(/\{missing\}/g, missing.missingCount)
          .replace(/\{total\}/g, missing.requiredCount);
        missingDiv.textContent = `${missing.groupLabel}: ${detailsText}`;
        contentContainer.appendChild(missingDiv);
      });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getGroupLabel(groupId, summaryElement) {
    const groupContainer = summaryElement.closest("details");
    let groupLabel = "Unknown Group";

    if (groupContainer) {
      const localizedLabel = groupContainer.getAttribute(
        "data-localized-group-label"
      );
      if (localizedLabel) {
        groupLabel = localizedLabel;
      } else {
        let parent = groupContainer.parentElement;
        while (parent) {
          const parentLocalizedLabel = parent.getAttribute(
            "data-localized-group-label"
          );
          if (parentLocalizedLabel) {
            groupLabel = parentLocalizedLabel;
            break;
          }
          parent = parent.parentElement;
        }

        if (groupLabel === "Unknown Group") {
          const titleDiv = groupContainer.querySelector(
            "summary .text-lg.font-semibold.text-gray-800"
          );
          if (titleDiv) {
            groupLabel = titleDiv.textContent.trim();
          } else {
            const summaryTag = groupContainer.querySelector(
              "summary span:not(.product-group-profile-summary-label):not(.product-group-profile-status-marker)"
            );
            if (summaryTag) {
              groupLabel = summaryTag.textContent.trim();
            }
          }
        }
      }
    }

    return groupLabel;
  }

  updateAllGroupSummaries() {
    const allSummaryLabels = this.element.querySelectorAll(
      ".product-group-profile-summary-label"
    );

    const groupIds = new Set();
    this.element
      .querySelectorAll(
        ".product-group-profile-select, .product-group-profile-checkbox, .product-group-profile-quantity"
      )
      .forEach((input) => {
        if (input.dataset.groupId) {
          groupIds.add(input.dataset.groupId);
        }
      });

    if (groupIds.size === 0) {
      allSummaryLabels.forEach((label) => {
        if (label.dataset.groupId) {
          groupIds.add(label.dataset.groupId);
        }
      });
    }

    groupIds.forEach((groupId) => this.updateGroupSummary(groupId));
  }

  updateGroupSummary(groupId) {
    if (!groupId) return;

    const selects = this.element.querySelectorAll(
      `.product-group-profile-select[data-group-id='${groupId}']`
    );
    const checkboxes = this.element.querySelectorAll(
      `.product-group-profile-checkbox[data-group-id='${groupId}']`
    );

    const quantitySelects = this.element.querySelectorAll(
      `select[data-group-id='${groupId}'][data-sku-id]`
    );

    const summaryLabel = this.element.querySelector(
      `.product-group-profile-summary-label[data-group-id='${groupId}']`
    );
    const statusMarkers = this.element.querySelectorAll(
      `.product-group-profile-status-marker[data-group-id='${groupId}']`
    );

    if (!summaryLabel) {
      return;
    }

    if (quantitySelects.length > 0) {
      return;
    }

    let totalCount = 0;
    if (summaryLabel.dataset.requiredCount) {
      totalCount = parseInt(summaryLabel.dataset.requiredCount, 10) || 0;
    }

    let selectedCount = 0;

    selects.forEach((s) => {
      if (s.value && s.value !== "") {
        selectedCount += 1;
      }
    });

    checkboxes.forEach((c) => {
      if (c.checked) {
        selectedCount += 1;
      }
    });

    const template = this.hasXOfYSelectedValue
      ? this.xOfYSelectedValue
      : "{selected} of {total} elements selected";

    const newText = template
      .replace(/%\{selected\}/g, selectedCount)
      .replace(/%\{total\}/g, totalCount)
      .replace(/\{selected\}/g, selectedCount)
      .replace(/\{total\}/g, totalCount);

    const allSummaryLabels = this.element.querySelectorAll(
      `.product-group-profile-summary-label[data-group-id='${groupId}']`
    );

    allSummaryLabels.forEach((label) => {
      // eslint-disable-next-line no-param-reassign
      label.textContent = newText;
      // eslint-disable-next-line no-param-reassign
      label.dataset.selectedCount = selectedCount;
    });

    if (statusMarkers.length > 0) {
      const selectionComplete = selectedCount >= totalCount && totalCount > 0;
      statusMarkers.forEach((statusMarker) => {
        if (selectionComplete) {
          statusMarker.classList.remove("hidden");
        } else {
          statusMarker.classList.add("hidden");
        }
      });
    }
  }
}
